import type {AutoValue, LocationCoordinates, OmitStrict, RichTextContent, SerializedDateTimeValueAsString, Values, HideableValue} from '@wix/devzai-utils-common';
import type {DoppeDtoContactMethod} from './doppe-dto-contact-method';
import type {DoppeDtoUserContactMetadata} from './doppe-dto-user-settings';
import {
    autoValueCreate,
    autoValueIsAutoValue,
    objectOmit,
    stringTrimPrefix,
    uuidCreate, hideableValueCreateHidden, hideableValueGetVisibleValue } from '@wix/devzai-utils-common';
import type {WixImageResource} from '@wix/devzai-common-wix';
import type {DoppeActionCtaButtonSettings} from './doppe-action-cta-button-settings';
import type {DoppeActionPrice} from './doppe-action-price';
import type {DoppeCheckoutActionCheckoutSettings} from './doppe-checkout-action-settings';
import {doppeCheckoutActionCheckoutSettingsCreateStripe, getDefaultDoppeCheckoutSettingsForAction} from './doppe-checkout-action-settings';
import type {DoppeActionCtaButtonsSettings} from './doppe-action-cta-buttons-settings';

import type {DoppeHtmlSitePagesType} from './doppe-html-site';
import type {BymoPagePropsAiStringKey} from '../bymo-page-props-metadata/bymo-page-props-ai-strings';
import type {DoppeBusinessEntityReference} from './doppe-business-entity';
import {DoppeBusinessEntity} from './doppe-business-entity';
import {
    BymoPagePropsContentSettingContextDependentSemanticMeaning,
    BymoPagePropsContentSettingsFilterOptions,
    BymoPagePropsContentSettingsMetadata
} from "../bymo-page-props-metadata/bymo-page-props-metadata";
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    bymoPagePropsFilterContentSettings,
    BymoPagePropsSpecialContentSettingMetadata
} from "../bymo-page-props-metadata/bymo-page-props-metadata";

/**
 * The types of CTA buttons that can be used in any action that supports CTA buttons.
 */
export const DoppeActionCommonCTATypes = {
    Link: 'Link',
    Whatsapp: 'Whatsapp',
    Call: 'Call',
    Navigate: 'Navigate',
    /**
     * NavigateV2 - Navigate button with precise location coordinates.
     * Uses lat/lon for accurate navigation instead of text address.
     */
    NavigateV2: 'NavigateV2',
    Email: 'Email',
    FacebookMessage: 'FacebookMessage',
    InstagramMessage: 'InstagramMessage',
    Share: 'Share',
    Checkout: 'Checkout',
    IsracardPay: 'IsracardPay',
    WixEcomCheckout: 'WixEcomCheckout',
    /**
     * ContactButton - opens a modal containing a list of contact methods.
     * Clicking a method opens the contact-method actions panel (without "Save contact").
     */
    ContactButton: 'ContactButton',
    /**
     * DownloadFile - downloads a Wix Media file when clicked.
     * Can optionally be gated behind a Wix Checkout payment via the
     * hideable `paymentSettings` field. When the payment gate is visible,
     * the viewer opens a Wix Checkout for a custom Digital line item and
     * surfaces a "Claim download" modal once the buyer returns with a
     * paid order. When hidden, the viewer mints a signed download URL
     * server-side and triggers the download immediately.
     */
    DownloadFile: 'DownloadFile',
    /**
     * AddToCalendar - opens a picker letting the visitor add a configured
     * calendar event to Google, Apple, Outlook, Yahoo, or download an .ics.
     * Event details (title, start/end, description, location, all-day,
     * timezone) are stored on the button.
     */
    AddToCalendar: 'AddToCalendar',
} as const;

export type DoppeActionCommonCTATypes = Values<typeof DoppeActionCommonCTATypes>;


/**
 * @deprecated
 */
export const DoppeActionDeprecatedCTATypes = {
    /** @deprecated Use Link type with "Same as source URL" instead */
    OpenBlogPost: 'OpenBlogPost',
    /** @deprecated Use Link type with "Same as source URL" instead */
    OpenProduct: 'OpenProduct',
    /** @deprecated Use Link type with "Same as source URL" instead */
    OpenService: 'OpenService',
    /** @deprecated Use Link type with "Same as source URL" instead */
    OpenEvent: 'OpenEvent',
    /** @deprecated Use Link type with "Same as source URL" instead */
    OpenPricingPlan: 'OpenPricingPlan',
    /**
     * @deprecated Use {@link DoppeActionCTAType.Link} with {@link DoppeLinkSpec.Page} instead.
     * Existing OpenPage buttons are migrated to Link with DoppeLinkSpec.Page via OpenPageCtaToLinkSpec migration.
     */
    OpenPage: 'OpenPage',
} as const;

export type DoppeActionDeprecatedCTATypes = Values<typeof DoppeActionDeprecatedCTATypes>;

export const DoppeActionTypeSpecificCTATypes = {
    SaveContact: 'SaveContact',
    FormSubmit: 'FormSubmit',
    BuyNow: 'BuyNow',
    AddToCart: 'AddToCart',
    BookNow: 'BookNow',
    Register: 'Register',
    /**
     * DownloadMainFile - the auto-seeded, non-deletable primary download CTA
     * used inside the `DownloadFilePreview` action. It carries no own settings:
     * `fileId` and `paymentSettings` are sourced from the parent action's
     * `actionSettings` at click time, and the button label uses the standard
     * `MultipleButton.text` field. Only valid inside `DownloadFilePreview`.
     */
    DownloadMainFile: 'DownloadMainFile',
} as const;

export type DoppeActionTypeSpecificCTATypes = Values<typeof DoppeActionTypeSpecificCTATypes>;

export const DoppeActionCTAType = {
    ...DoppeActionDeprecatedCTATypes,
    ...DoppeActionCommonCTATypes,
    ...DoppeActionTypeSpecificCTATypes
} as const;

export type DoppeActionCTAType = Values<typeof DoppeActionCTAType>;

export const DoppeActionCTATypeLinkAutoSource = {
    SameAsSourceURL: 'SameAsSourceURL',
} as const;

export type DoppeActionCTATypeLinkAutoSource = Values<typeof DoppeActionCTATypeLinkAutoSource>;

/**
 * Map from each {@link DoppeActionCTATypeLinkAutoSource} variant to its
 * LLM-facing description key in `bymo-page-props-ai-strings.json`.
 *
 * See `DoppeProfileNameAutoSourceAiStringKeys` for the contract
 * and how the AI schema emitter consumes the map.
 */
export const DoppeActionCTATypeLinkAutoSourceAiStringKeys = {
    [DoppeActionCTATypeLinkAutoSource.SameAsSourceURL]: 'autoSource.link.SameAsSourceURL',
} as const satisfies Record<DoppeActionCTATypeLinkAutoSource, BymoPagePropsAiStringKey>;

export const DoppeActionCTATypeLinkOpenType = {
    NewTab: 'NewTab',
    SameTab: 'SameTab',
    InPage: 'InPage',
} as const;

export type DoppeActionCTATypeLinkOpenType = Values<typeof DoppeActionCTATypeLinkOpenType>;

/**
 * Link spec type discriminator - determines what type of link spec object it is
 */
export const DoppeLinkSpecType = {
    /**
     * Internal page (inner page or vertical page)
     */
    Page: 'Page',
    /**
     * Vertical entity item (Product, Blog Post, Event, Service)
     */
    Entity: 'Entity',

    URL: 'Url',
} as const;

export type DoppeLinkSpecType = Values<typeof DoppeLinkSpecType>;

/**
 * Entity types for vertical item links
 */
export const DoppeLinkSpecEntityType = {
    Product: 'Product',
    BlogPost: 'BlogPost',
    Event: 'Event',
    Service: 'Service',
    PricingPlan: 'PricingPlan'
} as const;

export type DoppeLinkSpecEntityType = Values<typeof DoppeLinkSpecEntityType>;

/**
 * Link specification - can be a URL string or an object describing a page/entity
 */
export type DoppeLinkSpec = string | DoppeLinkSpec.Page | DoppeLinkSpec.Entity;

export namespace DoppeLinkSpec {
    /**
     * Page link specification - links to an internal page
     */
    export interface Page {
        type: typeof DoppeLinkSpecType.Page;
        /**
         * The page ID to navigate to (inner page or vertical page ID)
         */
        pageId: string;
    }

    /**
     * Entity link specification - discriminated union by entityType.
     * Each entity type has its own set of valid open actions.
     */
    export type Entity =
        | Entity.Product
        | Entity.Service
        | Entity.Event
        | Entity.BlogPost
        | Entity.PricingPlan;

    export namespace Entity {
        interface Base<ENTITY_TYPE extends DoppeLinkSpecEntityType> {
            type: typeof DoppeLinkSpecType.Entity;
            entityType: ENTITY_TYPE;
            /**
             * The entity item ID
             */
            entityId: string;
        }

        export interface Product extends Base<typeof DoppeLinkSpecEntityType.Product> {
        }

        export interface Service extends Base<typeof DoppeLinkSpecEntityType.Service> {
        }

        export interface Event extends Base<typeof DoppeLinkSpecEntityType.Event> {
        }

        export interface BlogPost extends Base<typeof DoppeLinkSpecEntityType.BlogPost> {
        }

        export interface PricingPlan extends Base<typeof DoppeLinkSpecEntityType.PricingPlan> {
        }
    }
}

/**
 * Type guard to check if a link spec is a string URL
 */
export function doppeLinkSpecIsUrl(linkSpec: DoppeLinkSpec): linkSpec is string {
    return typeof linkSpec === 'string';
}

/**
 * Type guard to check if a link spec is a page link
 */
export function doppeLinkSpecIsPage(linkSpec: DoppeLinkSpec): linkSpec is DoppeLinkSpec.Page {
    return typeof linkSpec === 'object' && linkSpec !== null && linkSpec.type === DoppeLinkSpecType.Page;
}

/**
 * Type guard to check if a link spec is an entity link
 */
export function doppeLinkSpecIsEntity(linkSpec: DoppeLinkSpec): linkSpec is DoppeLinkSpec.Entity {
    return typeof linkSpec === 'object' && linkSpec !== null && linkSpec.type === DoppeLinkSpecType.Entity;
}

/**
 * Gets the link type from a link spec ('Url', 'Page', or 'Entity')
 */
export function doppeLinkSpecGetType(linkSpec: DoppeLinkSpec): DoppeLinkSpecType {
    if (doppeLinkSpecIsUrl(linkSpec)) {
        return DoppeLinkSpecType.URL;
    }
    return linkSpec.type;
}

/**
 * Creates a default empty link spec (empty URL string)
 */
export function doppeLinkSpecCreateDefault(): DoppeLinkSpec {
    return '#';
}

/**
 * Creates a page link spec
 */
export function doppeLinkSpecCreatePage(pageId: string): DoppeLinkSpec.Page {
    return {
        type: DoppeLinkSpecType.Page,
        pageId: pageId
    };
}

/**
 * Creates an entity link spec.
 * When called with a specific entityType literal, returns the corresponding Entity variant.
 */
export function doppeLinkSpecCreateEntity(entityType: typeof DoppeLinkSpecEntityType.Product, entityId: string): DoppeLinkSpec.Entity.Product;
export function doppeLinkSpecCreateEntity(entityType: typeof DoppeLinkSpecEntityType.Service, entityId: string): DoppeLinkSpec.Entity.Service;
export function doppeLinkSpecCreateEntity(entityType: typeof DoppeLinkSpecEntityType.Event, entityId: string): DoppeLinkSpec.Entity.Event;
export function doppeLinkSpecCreateEntity(entityType: typeof DoppeLinkSpecEntityType.BlogPost, entityId: string): DoppeLinkSpec.Entity.BlogPost;
export function doppeLinkSpecCreateEntity(entityType: typeof DoppeLinkSpecEntityType.PricingPlan, entityId: string): DoppeLinkSpec.Entity.PricingPlan;
export function doppeLinkSpecCreateEntity(entityType: DoppeLinkSpecEntityType, entityId: string): DoppeLinkSpec.Entity;
export function doppeLinkSpecCreateEntity(entityType: DoppeLinkSpecEntityType, entityId: string): DoppeLinkSpec.Entity {
    return {
        type: DoppeLinkSpecType.Entity,
        entityType: entityType,
        entityId: entityId,
    } as DoppeLinkSpec.Entity;
}

/**
 * Extracts the destination type from a DoppeLinkSpec for UI purposes
 */
export function doppeLinkSpecGetDestinationType(linkSource: DoppeActionCTATypeLinkSource): DoppeLinkSpecType {
    if (autoValueIsAutoValue(linkSource)) {
        return DoppeLinkSpecType.URL;
    }
    return doppeLinkSpecGetType(linkSource);
}

/**
 * Checks if a link source is a DoppeLinkSpec object (Page or Entity, not a string URL or AutoValue)
 */
export function doppeLinkSpecIsLinkSpecObject(linkSource: DoppeActionCTATypeLinkSource): linkSource is DoppeLinkSpec.Page | DoppeLinkSpec.Entity {
    if (autoValueIsAutoValue(linkSource)) {
        return false;
    }
    return typeof linkSource === 'object' && linkSource !== null && 'type' in linkSource;
}

/**
 * Extracts page ID from a DoppeLinkSpec if it's a page link
 */
export function doppeLinkSpecGetPageId(linkSource: DoppeActionCTATypeLinkSource): string | null {
    if (autoValueIsAutoValue(linkSource)) {
        return null;
    }
    if (doppeLinkSpecIsPage(linkSource)) {
        return linkSource.pageId;
    }
    return null;
}

/**
 * Extracts entity info from a DoppeLinkSpec if it's an entity link
 */
export function doppeLinkSpecGetEntityInfo(linkSource: DoppeActionCTATypeLinkSource): { entityType: DoppeLinkSpecEntityType; entityId: string } | null {
    if (autoValueIsAutoValue(linkSource)) {
        return null;
    }
    if (doppeLinkSpecIsEntity(linkSource)) {
        return {
            entityType: linkSource.entityType,
            entityId: linkSource.entityId
        };
    }
    return null;
}

/**
 * Extracts URL string from a DoppeLinkSpec if it's a URL
 */
export function doppeLinkSpecGetUrlString(linkSource: DoppeActionCTATypeLinkSource): string | null {
    if (autoValueIsAutoValue(linkSource)) {
        return null; // AutoValue means "same as source URL"
    }
    if (doppeLinkSpecIsUrl(linkSource)) {
        return linkSource;
    }
    return null;
}

/**
 * Type guard to check if an entity link spec is a product entity
 */
export function doppeLinkSpecEntityIsProduct(entity: DoppeLinkSpec.Entity): entity is DoppeLinkSpec.Entity.Product {
    return entity.entityType === DoppeLinkSpecEntityType.Product;
}

/**
 * Type guard to check if an entity link spec is a service entity
 */
export function doppeLinkSpecEntityIsService(entity: DoppeLinkSpec.Entity): entity is DoppeLinkSpec.Entity.Service {
    return entity.entityType === DoppeLinkSpecEntityType.Service;
}

/**
 * Type guard to check if an entity link spec is an event entity
 */
export function doppeLinkSpecEntityIsEvent(entity: DoppeLinkSpec.Entity): entity is DoppeLinkSpec.Entity.Event {
    return entity.entityType === DoppeLinkSpecEntityType.Event;
}

/**
 * Checks if a DoppeLinkSpec is empty (no valid destination)
 * - For URL strings: empty string is empty
 * - For Page links: missing pageId is empty
 * - For Entity links: missing entityId is empty
 */
export function doppeLinkSpecIsEmpty(linkSpec: DoppeLinkSpec): boolean {
    if (doppeLinkSpecIsUrl(linkSpec)) {
        return !linkSpec || linkSpec.trim() === '';
    }
    if (doppeLinkSpecIsPage(linkSpec)) {
        return !linkSpec.pageId;
    }
    if (doppeLinkSpecIsEntity(linkSpec)) {
        return !linkSpec.entityId;
    }
    return true;
}

/**
 * Link source for CTA buttons - can be an AutoValue or a DoppeLinkSpec
 */
export type DoppeActionCTATypeLinkSource = AutoValue<DoppeActionCTATypeLinkAutoSource> | DoppeLinkSpec;

/**
 * Resolves a DoppeLinkSpec to a DoppeBusinessEntityReference if it's an entity link.
 * Returns null for URL or Page links.
 */
export function doppeLinkSpecResolveBusinessEntityReference(linkSpec: DoppeLinkSpec): DoppeBusinessEntityReference | null {
    if (!doppeLinkSpecIsEntity(linkSpec)) {
        return null;
    }

    const entityType = linkSpec.entityType;
    const entityId = linkSpec.entityId;

    if (!entityId) {
        return null;
    }

    switch (entityType) {
        case DoppeLinkSpecEntityType.Product:
            return {
                businessEntity: DoppeBusinessEntity.HoppProduct,
                id: entityId
            };
        case DoppeLinkSpecEntityType.Service:
            return {
                businessEntity: DoppeBusinessEntity.HoppService,
                id: entityId
            };
        case DoppeLinkSpecEntityType.BlogPost:
            return {
                businessEntity: DoppeBusinessEntity.HoppBlogPost,
                id: entityId
            };
        case DoppeLinkSpecEntityType.Event:
            return {
                businessEntity: DoppeBusinessEntity.HoppEvent,
                id: entityId
            };
        default:
            return null;
    }
}


export type DoppeActionCTAButtonSingle = DoppeActionCTAButton.SingleButton;
export type DoppeActionCTAButtonMultiple = DoppeActionCTAButton.MultipleButton;

export type DoppeActionCTAButton = DoppeActionCTAButton.BaseType;

export namespace DoppeActionCTAButton {

    export type BaseType = {
        settings: DoppeActionSettingsCTAButtonSettings;
    }

    export type SingleButton = DoppeActionCTAButton.BaseType & {
        text: HideableValue<string>;
        showButton: boolean;
    }

    export type MultipleButtonBase = DoppeActionCTAButton.BaseType & {
        isVisible: boolean;
    }

    export type MultipleButton = DoppeActionCTAButton.MultipleButtonBase & {
        text: string;
        isRequired: boolean;
        id: string;
    }

    export type MultipleButtonOverride = DoppeActionCTAButton.MultipleButtonBase & {
        useDefaultContent: boolean;
    }

    export type MultipleButtonWithOverride =
        DoppeActionCTAButton.MultipleButton
        & DoppeActionCTAButton.MultipleButtonOverride
        & {
        isDefaultButton: boolean;
    }
}

export function doppeActionCtaButtonCreateFromListAndOverride(rootId: string, actionCtaButton: DoppeActionCTAButton.MultipleButton, override: DoppeActionCTAButton.MultipleButtonOverride) {
    return {
        // `isRequired` is inherited from the template button (via `...actionCtaButton`),
        // NOT forced to `true`. Only structurally-locked buttons (e.g. the auto-seeded
        // `DownloadMainFile` CTA) carry `isRequired: true`; Share/Link template buttons keep
        // their own `false` so they stay hideable per-item. `MultipleButtonOverride` has no
        // `isRequired`, so `...override` won't clobber it.
        ...actionCtaButton,
        ...override,
        id: doppeActionCtaButtonCreateFromListAndOverrideId(rootId, actionCtaButton.id)
    } as DoppeActionCTAButton.MultipleButton;
}

export function doppeActionCtaButtonCreateFromListAndOverrideId(rootId: string, actionCtaButtonId: string) {
    return rootId + actionCtaButtonId;
}

export function doppeActionCtaButtonContactListOverrideId(rootId: string, actionCtaButtonId: string) {
    return stringTrimPrefix(actionCtaButtonId, rootId);
}

export function doppeActionCtaButtonConvertSingleToMultipleButtons(ctaButton: DoppeActionCTAButton.SingleButton) {
    return [{
        text: hideableValueGetVisibleValue(ctaButton.text, ''),
        isVisible: ctaButton.showButton,
        isRequired: true,
        id: uuidCreate(),
        settings: ctaButton.settings
    }] satisfies DoppeActionCTAButton.MultipleButton[];
}

export type DoppeActionSettingsCTAButtonSettings =
    | DoppeActionSettingsCTAButtonSettings.Link
    | DoppeActionSettingsCTAButtonSettings.Whatsapp
    | DoppeActionSettingsCTAButtonSettings.Call
    | DoppeActionSettingsCTAButtonSettings.Navigate
    | DoppeActionSettingsCTAButtonSettings.NavigateV2
    | DoppeActionSettingsCTAButtonSettings.Email
    | DoppeActionSettingsCTAButtonSettings.InstagramMessage
    | DoppeActionSettingsCTAButtonSettings.FacebookMessage
    | DoppeActionSettingsCTAButtonSettings.SaveContact
    | DoppeActionSettingsCTAButtonSettings.Share
    | DoppeActionSettingsCTAButtonSettings.Checkout
    | DoppeActionSettingsCTAButtonSettings.OpenProduct
    | DoppeActionSettingsCTAButtonSettings.OpenBlogPost
    | DoppeActionSettingsCTAButtonSettings.OpenService
    | DoppeActionSettingsCTAButtonSettings.OpenEvent
    | DoppeActionSettingsCTAButtonSettings.OpenPricingPlan
    | DoppeActionSettingsCTAButtonSettings.OpenPage
    | DoppeActionSettingsCTAButtonSettings.IsracardPay
    | DoppeActionSettingsCTAButtonSettings.WixEcomCheckout
    | DoppeActionSettingsCTAButtonSettings.FormSubmit
    | DoppeActionSettingsCTAButtonSettings.BuyNow
    | DoppeActionSettingsCTAButtonSettings.AddToCart
    | DoppeActionSettingsCTAButtonSettings.BookNow
    | DoppeActionSettingsCTAButtonSettings.Register
    | DoppeActionSettingsCTAButtonSettings.ContactButton
    | DoppeActionSettingsCTAButtonSettings.DownloadFile
    | DoppeActionSettingsCTAButtonSettings.DownloadMainFile
    | DoppeActionSettingsCTAButtonSettings.AddToCalendar;

export namespace DoppeActionSettingsCTAButtonSettings {

    export type Override = Pick<DoppeActionCTAButtonSingle, 'showButton' | 'settings'>

    type BaseType<T extends DoppeActionCTAType> = {
        type: T;
    }

    export type Link = BaseType<typeof DoppeActionCTAType.Link> & {
        /**
         * Link specification - can be:
         * - AutoValue for "Same as source URL"
         * - A string URL (e.g., 'https://example.com')
         * - A DoppeLinkSpec.Page object (e.g., { type: 'Page', pageId: '...' })
         * - A DoppeLinkSpec.Entity object (e.g., { type: 'Entity', entityType: 'Product', entityId: '...' })
         */
        url: DoppeActionCTATypeLinkSource;
        openType: DoppeActionCTATypeLinkOpenType;
    };

    export type IsracardPay = BaseType<typeof DoppeActionCTAType.IsracardPay> & {
        url: string;
        openType: DoppeActionCTATypeLinkOpenType;
    };

    /** @deprecated Use Link type with "Same as source URL" instead */
    export type OpenProduct = BaseType<typeof DoppeActionCTAType.OpenProduct> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    /** @deprecated Use Link type with "Same as source URL" instead */
    export type OpenBlogPost = BaseType<typeof DoppeActionCTAType.OpenBlogPost> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    /** @deprecated Use Link type with "Same as source URL" instead */
    export type OpenService = BaseType<typeof DoppeActionCTAType.OpenService> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    /** @deprecated Use Link type with "Same as source URL" instead */
    export type OpenEvent = BaseType<typeof DoppeActionCTAType.OpenEvent> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    /** @deprecated Use Link type with "Same as source URL" instead */
    export type OpenPricingPlan = BaseType<typeof DoppeActionCTAType.OpenPricingPlan> & {
        openType: DoppeActionCTATypeLinkOpenType;
    };

    export type OpenPage = BaseType<typeof DoppeActionCTAType.OpenPage> & {
        openType: DoppeActionCTATypeLinkOpenType;
        pageType: DoppeHtmlSitePagesType | null;
    };


    export type Whatsapp = BaseType<typeof DoppeActionCTAType.Whatsapp> & {
        phone: string;
        customMessage: HideableValue<string>;
    };

    export type Call = BaseType<typeof DoppeActionCTAType.Call> & {
        phone: string;
    };

    export type Email = BaseType<typeof DoppeActionCTAType.Email> & {
        email: string;
        customSubject: HideableValue<string>;
        customMessage: HideableValue<string>;
    };

    export type Navigate = BaseType<typeof DoppeActionCTAType.Navigate> & {
        address: string;
    };

    /**
     * NavigateV2 - Navigate button with precise location coordinates.
     * Uses lat/lon for accurate navigation instead of text address.
     */
    export type NavigateV2 = BaseType<typeof DoppeActionCTAType.NavigateV2> & {
        /**
         * The precise location coordinates for navigation.
         * When present, navigation URL uses lat/lon for accuracy.
         */
        locationCoordinates: LocationCoordinates | null;
        displayAddress: string;
    };

    export type FacebookMessage = BaseType<typeof DoppeActionCTAType.FacebookMessage> & {
        userName: string;
        customMessage: HideableValue<string>
    };

    export type InstagramMessage = BaseType<typeof DoppeActionCTAType.InstagramMessage> & {
        userName: string;
    };

    export type SaveContact = BaseType<typeof DoppeActionCTAType.SaveContact> & {
        notes: string;
        saveContactProfileImage: HideableValue<WixImageResource | null>
    }

    export type FormSubmit = BaseType<typeof DoppeActionCTAType.FormSubmit>;

    export type BuyNow = BaseType<typeof DoppeActionCTAType.BuyNow> & {
        showAddToCart: boolean;
        showQuantitySelector: boolean;
    };

    export type AddToCart = BaseType<typeof DoppeActionCTAType.AddToCart> & {
        afterAddBehavior: DoppeAddToCartAfterAddBehavior;
    };

    export type BookNow = BaseType<typeof DoppeActionCTAType.BookNow>;

    export type Register = BaseType<typeof DoppeActionCTAType.Register>;

    export type Share = BaseType<typeof DoppeActionCTAType.Share> & {
        url: DoppeActionCTATypeLinkSource;
        title: string;
        description: string;
    }

    export type ContactButton = BaseType<typeof DoppeActionCTAType.ContactButton> & {
        contactMethods: DoppeDtoContactMethod[];
    }

    /**
     * DownloadFile CTA button - triggers a Wix Media file download on click,
     * optionally gated behind a Wix Checkout payment.
     */
    export type DownloadFile = BaseType<typeof DoppeActionCTAType.DownloadFile> & {
        /** Wix Media file id (Media Manager) of the private file to be downloaded. */
        fileId: string;
        /** Display name of the file (e.g. the original filename), shown in the editor. */
        fileName: string;
        /**
         * Optional payment gate. When hidden the click downloads the file
         * immediately via a server-minted signed URL. When visible the click
         * opens a Wix Checkout for the configured custom product; on return,
         * the viewer shows a claim-download modal that validates the order
         * and mints a signed URL.
         */
        paymentSettings: HideableValue<DownloadFile.PaymentSettings>;
    }

    export namespace DownloadFile {
        export interface PaymentSettings {
            productName: string;
            price: DoppeActionPrice;
            /**
             * Quantity for the custom line item. Almost always 1 — the field
             * exists only to keep parity with `WixEcomCheckout.CustomProduct`
             * (and Wix Checkout requires a positive integer).
             */
            quantity: number;
            /**
             * Optional human-facing description of what's being purchased, mapped
             * to the Wix Custom line item's `itemTypeDescription` and shown as the
             * item-type label in the Wix Checkout order summary. When empty, the
             * viewer falls back to a localized "Digital download" label.
             *
             * The custom line item is always created with `kind: Custom` (never
             * `DigitalProduct`) because metasites with Wix Stores installed reject
             * DIGITAL-preset custom line items with `MISSING_DIGITAL_FILE` — only
             * Wix Stores catalog products carry the required `digitalFile.fileId`.
             */
            customItemTypeDescription: HideableValue<string>;
        }
    }

    /**
     * DownloadMainFile CTA button - the auto-seeded, non-deletable primary
     * download CTA used inside the `DownloadFilePreview` action. It carries
     * no own settings: `fileId` and `paymentSettings` are sourced from the
     * parent action's `actionSettings` at click time. The button label uses
     * the standard `MultipleButton.text` field. Only valid inside
     * `DownloadFilePreview`.
     */
    export type DownloadMainFile = BaseType<typeof DoppeActionCTAType.DownloadMainFile>;

    /**
     * AddToCalendar CTA button - on click, opens a picker modal letting the
     * visitor add the configured event to Google, Apple, Outlook, Yahoo, or
     * download a generic `.ics` file.
     */
    export type AddToCalendar = BaseType<typeof DoppeActionCTAType.AddToCalendar> & {
        title: string;
        /** Date-only ISO string for the start (e.g. `2026-06-15`). */
        startDate: SerializedDateTimeValueAsString;
        /** "HH:MM" time-of-day for the start. Ignored when `allDay` is true. */
        startTime: string;
        /** Date-only ISO string for the end. */
        endDate: SerializedDateTimeValueAsString;
        /** "HH:MM" time-of-day for the end. Ignored when `allDay` is true. */
        endTime: string;
        description: HideableValue<string>;
        location: HideableValue<string>;
        /**
         * When true, the event is all-day: the time fields are not used in URL/ICS
         * output, and the UI hides them in the settings panel.
         */
        allDay: boolean;
        /**
         * IANA timezone (e.g. `America/New_York`). Defaults to the creator's
         * browser timezone at button-creation time; overridable by the creator.
         */
        timezone: string;
    };

    export type Checkout = BaseType<typeof DoppeActionCTAType.Checkout> & {
        price: DoppeActionPrice;
        name: HideableValue<string>;
        description: HideableValue<RichTextContent>;
        checkoutSettings: DoppeCheckoutActionCheckoutSettings;
    }

    /**
     * WixEcomCheckout CTA button - supports catalog items or custom products
     */
    export type WixEcomCheckout = WixEcomCheckout.CatalogItem | WixEcomCheckout.CustomProduct;

    export namespace WixEcomCheckout {
        interface Base<PRODUCT_TYPE extends WixEcomCheckoutProductType> {
            type: typeof DoppeActionCTAType.WixEcomCheckout;
            productType: PRODUCT_TYPE;
        }

        export interface CatalogItem extends Base<typeof WixEcomCheckoutProductType.CatalogItem> {
            productId: string;
        }

        export interface CustomProduct extends Base<typeof WixEcomCheckoutProductType.CustomProduct> {
            productName: string;
            price: DoppeActionPrice;
            quantity: number;
            itemKind: WixEcomCheckoutItemKind;
            customItemTypeDescription: HideableValue<string>;
        }
    }

}

/**
 * Product type for WixEcomCheckout - determines if using a catalog product or custom
 */
export const WixEcomCheckoutProductType = {
    CatalogItem: 'CatalogItem',
    CustomProduct: 'CustomProduct',
} as const;

export type WixEcomCheckoutProductType = Values<typeof WixEcomCheckoutProductType>;

/**
 * Item kind for custom products in WixEcomCheckout
 */
export const WixEcomCheckoutItemKind = {
    Physical: 'physical',
    Digital: 'digital',
    GiftCard: 'gift_card',
    Service: 'service',
    Custom: 'custom',
} as const;

export type WixEcomCheckoutItemKind = Values<typeof WixEcomCheckoutItemKind>;

export const DoppeAddToCartAfterAddBehavior = {
    OpenCart: 'OpenCart',
    ShowNotification: 'ShowNotification',
} as const;

export type DoppeAddToCartAfterAddBehavior = Values<typeof DoppeAddToCartAfterAddBehavior>;

/**
 * Filtered CTA button settings for metadata, replacing the WixEcomCheckout union
 * with only the CustomProduct variant. CatalogItem is excluded because it requires
 * a platform-specific productId that AI cannot generate.
 */
type DoppeActionCTAButtonSettingsForMetadata = Exclude<
    DoppeActionSettingsCTAButtonSettings,
    DoppeActionSettingsCTAButtonSettings.WixEcomCheckout.CatalogItem
>;

export const DoppeActionCTAButtonsSettingsMetadata = {
    [DoppeActionCTAType.Link]: {
        url: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.LinkSource, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.Link.url'}),
    },
    [DoppeActionCTAType.Whatsapp]: {
        phone: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Phone, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.Whatsapp.phone'}),
        customMessage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.Whatsapp.customMessage'}),
    },
    [DoppeActionCTAType.Call]: {
        phone: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Phone, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.Call.phone'}),
    },
    [DoppeActionCTAType.Navigate]: {
        address: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingContextDependentSemanticMeaning.EntityAddress),
    },
    [DoppeActionCTAType.NavigateV2]: {
        displayAddress: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.NavigateV2.displayAddress'}),
        locationCoordinates: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.LocationCoordinates, BymoPagePropsContentSettingSemanticMeaning.EntityAddress),
    },
    [DoppeActionCTAType.Email]: {
        email: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.Email.email'}),
        customSubject: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.Email.customSubject'}),
        customMessage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.Email.customMessage'}),
    },
    [DoppeActionCTAType.InstagramMessage]: {
        userName: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.InstagramMessage.userName'}),
    },
    [DoppeActionCTAType.FacebookMessage]: {
        userName: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.FacebookMessage.userName'}),
        customMessage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.FacebookMessage.customMessage'}),
    },
    [DoppeActionCTAType.SaveContact]: {
        notes: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.SaveContact.notes'}),
        saveContactProfileImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleImage, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.SaveContact.saveContactProfileImage'}),
    },
    [DoppeActionCTAType.FormSubmit]: {},
    [DoppeActionCTAType.BuyNow]: {
        showAddToCart: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.showAddToCart'}),
        showQuantitySelector: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.showQuantitySelector'}),
    },
    [DoppeActionCTAType.AddToCart]: {},
    [DoppeActionCTAType.BookNow]: {},
    [DoppeActionCTAType.Register]: {},
    [DoppeActionCTAType.Share]: {
        url: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.LinkSource, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.Share.url'}),
        title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.Share.title'}),
        description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.Share.description'}),
    },
    [DoppeActionCTAType.Checkout]: {
        price: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
        name: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.Checkout.name'}),
        description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.Checkout.description'}),
        checkoutSettings: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    },
    [DoppeActionCTAType.OpenBlogPost]: {},
    [DoppeActionCTAType.OpenProduct]: {},
    [DoppeActionCTAType.OpenService]: {},
    [DoppeActionCTAType.OpenEvent]: {},
    [DoppeActionCTAType.OpenPricingPlan]: {},
    [DoppeActionCTAType.OpenPage]: {},
    [DoppeActionCTAType.IsracardPay]: {
        url: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.IsracardPay.url'}),
    },
    [DoppeActionCTAType.WixEcomCheckout]: {
        productName: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.WixEcomCheckout.productName'}),
        customItemTypeDescription: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.WixEcomCheckout.customItemTypeDescription'}),
        price: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
        quantity: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Number, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.WixEcomCheckout.quantity'}),
        itemKind: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    },
    [DoppeActionCTAType.ContactButton]: {
        contactMethods: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.ContactMethods, BymoPagePropsContentSettingSemanticMeaning.EntityContactMethods),
    },
    [DoppeActionCTAType.DownloadFile]: {
        fileId: BymoPagePropsSpecialContentSettingMetadata.Ignored,
        fileName: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.DownloadFile.fileName'}),
        paymentSettings: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    },
    [DoppeActionCTAType.DownloadMainFile]: {
        // No own settings beyond `type`: the file id and payment gate live on
        // the parent `DownloadFilePreview` action and are derived at click time.
    },
    [DoppeActionCTAType.AddToCalendar]: {
        title: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.AddToCalendar.title'}),
        startDate: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.DateTimeString, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.AddToCalendar.startDate'}),
        startTime: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.AddToCalendar.startTime'}),
        endDate: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.DateTimeString, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.AddToCalendar.endDate'}),
        endTime: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.AddToCalendar.endTime'}),
        description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.AddToCalendar.description'}),
        location: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.AddToCalendar.location'}),
        allDay: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.AddToCalendar.allDay'}),
        timezone: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.AddToCalendar.timezone'}),
    },
} satisfies {
    [BUTTON in DoppeActionCTAButtonSettingsForMetadata as BUTTON['type']]: BymoPagePropsContentSettingsMetadata<OmitStrict<BUTTON, 'type'>>;
}

export const DoppeActionCTAButtonsHideableSettingsNames: Partial<Record<DoppeActionCTAType, Record<string, true>>> = {
    [DoppeActionCTAType.Whatsapp]: { customMessage: true },
    [DoppeActionCTAType.Email]: { customSubject: true, customMessage: true },
    [DoppeActionCTAType.FacebookMessage]: { customMessage: true },
    [DoppeActionCTAType.SaveContact]: { saveContactProfileImage: true },
    [DoppeActionCTAType.Checkout]: { name: true, description: true },
    [DoppeActionCTAType.WixEcomCheckout]: { customItemTypeDescription: true },
    [DoppeActionCTAType.DownloadFile]: { paymentSettings: true },
    [DoppeActionCTAType.AddToCalendar]: { description: true, location: true },
};

export const DoppeActionCTAButtonGeneralSettingsMetadata = {
    text: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'cta.general.text'}),
    id: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    isRequired: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    isVisible: BymoPagePropsSpecialContentSettingMetadata.Ignored,
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionCTAButton.MultipleButton> | BymoPagePropsSpecialContentSettingMetadata;

export function doppeActionCTAButtonFilterContentSettings(
    actionCTAButton: DoppeActionCTAButton.MultipleButton,
    options: BymoPagePropsContentSettingsFilterOptions
) {
    return {
        ...bymoPagePropsFilterContentSettings(objectOmit(actionCTAButton, ['id']), DoppeActionCTAButtonGeneralSettingsMetadata, options),
        settings: bymoPagePropsFilterContentSettings(actionCTAButton.settings, DoppeActionCTAButtonsSettingsMetadata[actionCTAButton.settings.type], options)
    }
}

export function doppeActionMultipleCTAButtonIsVisible(ctaButton: DoppeActionCTAButton.MultipleButton) {
    return ctaButton.isVisible;
}

export function doppeActionCTAButtonIsVisible(ctaButton: DoppeActionCTAButton.SingleButton) {
    return ctaButton.showButton;
}

export function doppeActionCTAButtonCreateLinkSameAsSource(options: {
    text: string,
    hidden?: boolean
}) {

    const {
        text,
        hidden = false
    } = options;

    return {
        settings: {
            type: DoppeActionCTAType.Link,
            url: autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL),
            openType: DoppeActionCTATypeLinkOpenType.SameTab,
        },
        text: text,
        showButton: !hidden
    } satisfies DoppeActionCTAButton.SingleButton;
}

export function doppeActionMultipleCTAButtonCreateLinkSameAsSource(options: {
    text: string,
    hidden?: boolean
    isRequired?: boolean
}) {

    const {
        text,
        hidden = false,
        isRequired = false
    } = options;

    return {
        settings: {
            type: DoppeActionCTAType.Link,
            url: autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL),
            openType: DoppeActionCTATypeLinkOpenType.SameTab,
        },
        text: text,
        isVisible: !hidden,
        isRequired: isRequired,
        id: uuidCreate(),
    } satisfies DoppeActionCTAButton.MultipleButton
}

export function doppeActionMultipleCTAButtonCreate(options: {
    text: string,
    hidden?: boolean
    isRequired?: boolean,
    settings: DoppeActionSettingsCTAButtonSettings;
}) {

    const {
        text,
        hidden = false,
        isRequired = false,
        settings
    } = options;

    return {
        settings: settings,
        text: text,
        isVisible: !hidden,
        isRequired: isRequired,
        id: uuidCreate(),
    } satisfies DoppeActionCTAButton.MultipleButton
}

export function doppeActionCtaButtonResetSettingsForLockNew<T extends DoppeActionCTAButton.BaseType>(ctaButton: T) {
    const isSameSourceURl = (
            ctaButton.settings.type === DoppeActionCTAType.Link ||
            ctaButton.settings.type === DoppeActionCTAType.Share
        )
        && autoValueIsAutoValue(ctaButton.settings.url);
    ctaButton.settings = doppeActionCtaButtonTypeGetDefaultSettings(ctaButton.settings.type, isSameSourceURl);
}

export function doppeActionCtaButtonResetSettingsForLock(actionSettings: DoppeActionCtaButtonSettings) {
    const isSameSourceURl = (
            actionSettings.ctaButton.settings.type === DoppeActionCTAType.Link ||
            actionSettings.ctaButton.settings.type === DoppeActionCTAType.Share
        )
        && autoValueIsAutoValue(actionSettings.ctaButton.settings.url
        );
    actionSettings.ctaButton.settings = doppeActionCtaButtonTypeGetDefaultSettings(actionSettings.ctaButton.settings.type, isSameSourceURl);
}

export function doppeActionCtaButtonsResetSettingsForLock(actionSettings: DoppeActionCtaButtonsSettings) {
    actionSettings.ctaButtons.forEach(ctaButton => {
        doppeActionCtaButtonResetSettingsForLockNew(ctaButton);
    });
}

export function doppeActionCtaButtonTypeGetDefaultSettings(
    type: DoppeActionCTAType,
    hasSourceURL: boolean,
    hoppPages?: DoppeHtmlSitePagesType[]
) {

    switch (type) {
        case DoppeActionCTAType.Checkout: {
            return {
                type: DoppeActionCTAType.Checkout,
                price: {
                    actualPrice: 20,
                    currencyCode: 'USD'
                },
                name: hideableValueCreateHidden(''),
                description: hideableValueCreateHidden(''),
                checkoutSettings: doppeCheckoutActionCheckoutSettingsCreateStripe(
                    getDefaultDoppeCheckoutSettingsForAction()
                )
            } satisfies DoppeActionSettingsCTAButtonSettings.Checkout;
        }
        case DoppeActionCTAType.Whatsapp: {
            return {
                type: DoppeActionCTAType.Whatsapp,
                phone: '',
                customMessage: '',
            };
        }
        case DoppeActionCTAType.Call: {
            return {
                type: DoppeActionCTAType.Call,
                phone: '',
            };
        }
        case DoppeActionCTAType.Share:
            return {
                type: DoppeActionCTAType.Share,
                url: hasSourceURL ? autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL) : '',
                title: '',
                description: '',
            };
        case DoppeActionCTAType.Link:
            return {
                type: DoppeActionCTAType.Link,
                url: hasSourceURL ? autoValueCreate(DoppeActionCTATypeLinkAutoSource.SameAsSourceURL) : '',
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        case DoppeActionCTAType.IsracardPay:
            return {
                type: DoppeActionCTAType.IsracardPay,
                url: '',
                openType: DoppeActionCTATypeLinkOpenType.InPage,
            };
        case DoppeActionCTAType.Email: {
            return {
                type: DoppeActionCTAType.Email,
                email: '',
                customMessage: '',
                customSubject: ''
            };
        }
        case DoppeActionCTAType.Navigate: {
            return {
                type: DoppeActionCTAType.Navigate,
                address: '',
            };
        }
        case DoppeActionCTAType.NavigateV2: {
            return {
                type: DoppeActionCTAType.NavigateV2,
                locationCoordinates: null,
                displayAddress: '',
            };
        }
        case DoppeActionCTAType.FacebookMessage: {
            return {
                type: DoppeActionCTAType.FacebookMessage,
                userName: '',
                customMessage: '',
            };
        }
        case DoppeActionCTAType.InstagramMessage: {
            return {
                type: DoppeActionCTAType.InstagramMessage,
                userName: '',
            };
        }
        case DoppeActionCTAType.SaveContact: {
            return {
                type: DoppeActionCTAType.SaveContact,
                notes: '',
                saveContactProfileImage: hideableValueCreateHidden(null),
            };
        }
        case DoppeActionCTAType.FormSubmit: {
            return {
                type: DoppeActionCTAType.FormSubmit,
            };
        }
        case DoppeActionCTAType.BuyNow: {
            return {
                type: DoppeActionCTAType.BuyNow,
                showAddToCart: true,
                showQuantitySelector: true,
            };
        }
        case DoppeActionCTAType.AddToCart: {
            return {
                type: DoppeActionCTAType.AddToCart,
                afterAddBehavior: DoppeAddToCartAfterAddBehavior.OpenCart,
            };
        }
        case DoppeActionCTAType.BookNow: {
            return {
                type: DoppeActionCTAType.BookNow,
            };
        }
        case DoppeActionCTAType.Register: {
            return {
                type: DoppeActionCTAType.Register,
            };
        }
        case DoppeActionCTAType.OpenProduct: {
            return {
                type: DoppeActionCTAType.OpenProduct,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.OpenPage: {
            return {
                type: DoppeActionCTAType.OpenPage,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
                pageType: hoppPages?.[0] ?? null,
            };
        }
        case DoppeActionCTAType.OpenBlogPost: {
            return {
                type: DoppeActionCTAType.OpenBlogPost,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.OpenService: {
            return {
                type: DoppeActionCTAType.OpenService,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.OpenEvent: {
            return {
                type: DoppeActionCTAType.OpenEvent,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.OpenPricingPlan: {
            return {
                type: DoppeActionCTAType.OpenPricingPlan,
                openType: DoppeActionCTATypeLinkOpenType.SameTab,
            };
        }
        case DoppeActionCTAType.WixEcomCheckout: {
            return {
                type: DoppeActionCTAType.WixEcomCheckout,
                productType: WixEcomCheckoutProductType.CustomProduct,
                productName: '',
                price: {
                    actualPrice: 20,
                    currencyCode: 'USD'
                },
                quantity: 1,
                itemKind: WixEcomCheckoutItemKind.Custom,
                customItemTypeDescription: hideableValueCreateHidden(''),
            } satisfies DoppeActionSettingsCTAButtonSettings.WixEcomCheckout;
        }
        case DoppeActionCTAType.ContactButton: {
            return {
                type: DoppeActionCTAType.ContactButton,
                contactMethods: [],
            } satisfies DoppeActionSettingsCTAButtonSettings.ContactButton;
        }
        case DoppeActionCTAType.DownloadFile: {
            return {
                type: DoppeActionCTAType.DownloadFile,
                fileId: '',
                fileName: '',
                paymentSettings: hideableValueCreateHidden({
                    productName: '',
                    price: {
                        actualPrice: 5,
                        currencyCode: 'USD',
                    },
                    quantity: 1,
                    customItemTypeDescription: hideableValueCreateHidden(''),
                }),
            } satisfies DoppeActionSettingsCTAButtonSettings.DownloadFile;
        }
        case DoppeActionCTAType.DownloadMainFile: {
            return {
                type: DoppeActionCTAType.DownloadMainFile,
            } satisfies DoppeActionSettingsCTAButtonSettings.DownloadMainFile;
        }
        case DoppeActionCTAType.AddToCalendar: {
            const todayDate = new Date().toISOString().slice(0, 10);
            return {
                type: DoppeActionCTAType.AddToCalendar,
                title: '',
                startDate: todayDate,
                startTime: '10:00',
                endDate: todayDate,
                endTime: '11:00',
                description: hideableValueCreateHidden(''),
                location: hideableValueCreateHidden(''),
                allDay: false,
                timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC',
            } satisfies DoppeActionSettingsCTAButtonSettings.AddToCalendar;
        }
    }
}

export function doppeActionCtaButtonTypeOpenUrl(ctaSettings: DoppeActionSettingsCTAButtonSettings) {
    switch (ctaSettings.type) {
        case DoppeActionCTAType.Call:
        case DoppeActionCTAType.Email:
        case DoppeActionCTAType.Whatsapp:
        case DoppeActionCTAType.Navigate:
        case DoppeActionCTAType.NavigateV2:
        case DoppeActionCTAType.FacebookMessage: {
            return true;
        }
        case DoppeActionCTAType.InstagramMessage: {
            return true;
        }
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenPage:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.OpenPricingPlan:
        case DoppeActionCTAType.IsracardPay: {
            return ctaSettings.openType !== DoppeActionCTATypeLinkOpenType.InPage;
        }
        case DoppeActionCTAType.Link: {
            // For Link type, check if the url is a LinkSpec object (page/entity)
            const url = ctaSettings.url;
            if (!autoValueIsAutoValue(url) && !doppeLinkSpecIsUrl(url)) {
                // Page and Entity destinations are handled by internal router
                return false;
            }
            return ctaSettings.openType !== DoppeActionCTATypeLinkOpenType.InPage;
        }
        case DoppeActionCTAType.Checkout:
        case DoppeActionCTAType.Share:
        case DoppeActionCTAType.SaveContact:
        case DoppeActionCTAType.FormSubmit:
        case DoppeActionCTAType.BuyNow:
        case DoppeActionCTAType.AddToCart:
        case DoppeActionCTAType.BookNow:
        case DoppeActionCTAType.Register:
        case DoppeActionCTAType.WixEcomCheckout:
        case DoppeActionCTAType.ContactButton:
        case DoppeActionCTAType.DownloadFile:
        case DoppeActionCTAType.DownloadMainFile:
        case DoppeActionCTAType.AddToCalendar: {
            return false;
        }
    }
}

export function doppeActionCtaButtonLinkResolveTarget(ctaSettings: DoppeActionSettingsCTAButtonSettings) {

    switch (ctaSettings.type) {
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenPage:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.Link: {

            if (ctaSettings.openType === DoppeActionCTATypeLinkOpenType.NewTab) {
                return '_blank';
            } else if (ctaSettings.openType === DoppeActionCTATypeLinkOpenType.SameTab) {
                return '_self';
            } else {
                return undefined;
            }
        }
        default: {
            return undefined;
        }
    }
}

/**
 * Extracts contact metadata values from CTA button settings.
 * Returns only the fields relevant to the CTA type with non-empty values.
 */
export function doppeActionCtaButtonSettingsExtractContactMetadata(
    settings: DoppeActionSettingsCTAButtonSettings
): Partial<DoppeDtoUserContactMetadata> {
    switch (settings.type) {
        case DoppeActionCTAType.Whatsapp: {
            return settings.phone ? {whatsappPhone: settings.phone} : {};
        }
        case DoppeActionCTAType.Call: {
            return settings.phone ? {phone: settings.phone} : {};
        }
        case DoppeActionCTAType.Email: {
            return settings.email ? {email: settings.email} : {};
        }
        case DoppeActionCTAType.NavigateV2: {
            const result: Partial<DoppeDtoUserContactMetadata> = {};
            if (settings.displayAddress) {
                result.displayAddress = settings.displayAddress;
            }
            if (settings.locationCoordinates) {
                result.locationCoordinates = settings.locationCoordinates;
            }
            return result;
        }
        case DoppeActionCTAType.FacebookMessage: {
            return settings.userName ? {facebookUserName: settings.userName} : {};
        }
        case DoppeActionCTAType.InstagramMessage: {
            return settings.userName ? {instagramUserName: settings.userName} : {};
        }
        case DoppeActionCTAType.Link:
        case DoppeActionCTAType.Navigate:
        case DoppeActionCTAType.Share:
        case DoppeActionCTAType.Checkout:
        case DoppeActionCTAType.OpenPage:
        case DoppeActionCTAType.IsracardPay:
        case DoppeActionCTAType.WixEcomCheckout:
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.OpenPricingPlan:
        case DoppeActionCTAType.SaveContact:
        case DoppeActionCTAType.FormSubmit:
        case DoppeActionCTAType.BuyNow:
        case DoppeActionCTAType.AddToCart:
        case DoppeActionCTAType.BookNow:
        case DoppeActionCTAType.Register:
        case DoppeActionCTAType.ContactButton:
        case DoppeActionCTAType.DownloadFile:
        case DoppeActionCTAType.DownloadMainFile:
        case DoppeActionCTAType.AddToCalendar: {
            return {};
        }
    }
}

/**
 * Applies stored contact metadata to CTA button default settings,
 * filling in empty fields with previously saved values.
 */
export function doppeActionCtaButtonSettingsApplyContactMetadata(
    settings: DoppeActionSettingsCTAButtonSettings,
    contactMetadata: DoppeDtoUserContactMetadata | undefined
): DoppeActionSettingsCTAButtonSettings {
    if (!contactMetadata) {
        return settings;
    }

    switch (settings.type) {
        case DoppeActionCTAType.Whatsapp: {
            const resolvedPhone = contactMetadata.whatsappPhone ?? contactMetadata.phone;
            return resolvedPhone && !settings.phone
                ? {...settings, phone: resolvedPhone}
                : settings;
        }
        case DoppeActionCTAType.Call: {
            const resolvedPhone = contactMetadata.phone ?? contactMetadata.whatsappPhone;
            return resolvedPhone && !settings.phone
                ? {...settings, phone: resolvedPhone}
                : settings;
        }
        case DoppeActionCTAType.Email: {
            return contactMetadata.email && !settings.email
                ? {...settings, email: contactMetadata.email}
                : settings;
        }
        case DoppeActionCTAType.NavigateV2: {
            const hasStoredLocation = contactMetadata.displayAddress || contactMetadata.locationCoordinates;
            const isCurrentEmpty = !settings.displayAddress && !settings.locationCoordinates;
            if (hasStoredLocation && isCurrentEmpty) {
                return {
                    ...settings,
                    displayAddress: contactMetadata.displayAddress ?? settings.displayAddress,
                    locationCoordinates: contactMetadata.locationCoordinates ?? settings.locationCoordinates,
                };
            }
            return settings;
        }
        case DoppeActionCTAType.FacebookMessage: {
            return contactMetadata.facebookUserName && !settings.userName
                ? {...settings, userName: contactMetadata.facebookUserName}
                : settings;
        }
        case DoppeActionCTAType.InstagramMessage: {
            return contactMetadata.instagramUserName && !settings.userName
                ? {...settings, userName: stringTrimPrefix(contactMetadata.instagramUserName, '@')}
                : settings;
        }
        case DoppeActionCTAType.Link:
        case DoppeActionCTAType.Navigate:
        case DoppeActionCTAType.Share:
        case DoppeActionCTAType.Checkout:
        case DoppeActionCTAType.OpenPage:
        case DoppeActionCTAType.IsracardPay:
        case DoppeActionCTAType.WixEcomCheckout:
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.OpenPricingPlan:
        case DoppeActionCTAType.SaveContact:
        case DoppeActionCTAType.FormSubmit:
        case DoppeActionCTAType.BuyNow:
        case DoppeActionCTAType.AddToCart:
        case DoppeActionCTAType.BookNow:
        case DoppeActionCTAType.Register:
        case DoppeActionCTAType.ContactButton:
        case DoppeActionCTAType.DownloadFile:
        case DoppeActionCTAType.DownloadMainFile:
        case DoppeActionCTAType.AddToCalendar: {
            return settings;
        }
    }
}

